/**
 * The one-click demo letter (ATTEST-UX-AND-DEMO-READINESS-1 item 2).
 *
 * A demo of this wizard is a demo of its *refusals* — that is the product — but
 * a prospect has to see it produce a letter at least once, or the refusals read
 * as "it doesn't work" rather than "it won't say that". Filling five screens by
 * hand on a call does not survive contact with a live audience: one mistyped
 * assertion id and the demo ends on a red card.
 *
 * So this is a prefill, not a bypass. It writes the same form state a CPA would
 * have typed and then stops. Every gate still runs on the server, against the
 * same policy, in the same order. Nothing here can make a letter build that the
 * engine would otherwise refuse.
 *
 * The values live in `demoLetter.json` rather than in this file because
 * `tests/test_attest_demo_letter.py` reads the same JSON, maps it the way the
 * API maps a request body, and asserts `build_letter` returns an artifact. That
 * is what makes the demo *pre-validated* rather than merely plausible: if a
 * guardrail moves under it, CI goes red before a prospect sees a refusal on a
 * screen share. Two consumers, one file — which is the only reason the fixture
 * is JSON and not a `.ts` literal.
 *
 * Why these two assertions and no upload: they are the only two on this basis'
 * allow list that the policy marks `requires_attached_source: false`, so they
 * stand on the firm's own records plus a cited procedure. The other three need
 * a real filed return, and a demo is not a reason to let them through without
 * one. The Python test pins that reason, not just the outcome.
 */
import demo from '@/components/attest/demoLetter.json'
import type { ClassifyFormState } from '@/components/attest/classifyQuestions'
import {
  EMPTY_ISSUER_FORM,
  EMPTY_LETTER_FORM,
  type IssuerFormState,
  type LetterFormState,
  type StatementDraft,
} from '@/components/attest/formState'

/** Everything the wizard needs to be sitting on the demo, in one object. */
export interface DemoPrefill {
  typeId: string
  basis: string
  classify: ClassifyFormState
  letter: LetterFormState
  issuer: IssuerFormState
}

/** The letter type the demo needs. Offered only when the server lists it. */
export const DEMO_TYPE_ID: string = demo.type_id

/**
 * The fixture, mapped into wizard state.
 *
 * Built fresh on each call: the returned object is handed straight to
 * `useState` setters, and a shared mutable literal would let the CPA's first
 * edit write back into the demo itself.
 */
export function demoPrefill(): DemoPrefill {
  const statements: StatementDraft[] = demo.letter.statements.map(s => ({
    assertion_id: s.assertion_id,
    text: s.text,
    supportedByRecordsOnFile: s.supportedByRecordsOnFile,
    evidence: s.evidence.map(e => ({
      source_type: e.source_type,
      reference: e.reference,
      location: e.location,
      obtained_date: e.obtained_date,
    })),
  }))

  return {
    typeId: demo.type_id,
    basis: demo.engagement_basis,
    classify: {
      requestedItemsText: demo.classify.requestedItemsText,
      proceduresAgreedBySpecifiedParties:
        demo.classify.proceduresAgreedBySpecifiedParties,
      issuerIsAttestEligible: demo.classify.issuerIsAttestEligible,
      requestedAssuranceLevel: demo.classify.requestedAssuranceLevel,
    },
    letter: {
      ...EMPTY_LETTER_FORM,
      subjectEntity: demo.letter.subjectEntity,
      addressee: demo.letter.addressee,
      intendedUsersText: demo.letter.intendedUsersText,
      purpose: demo.letter.purpose,
      inputs: { ...demo.letter.inputs },
      statements,
      letterDate: demo.letter.letterDate,
      signerTitle: demo.letter.signerTitle,
      issuerAddressText: demo.letter.issuerAddressText,
      // Left empty on purpose. An engagement state is checked against the
      // signing firm's licence states, so naming one would make the demo build
      // for one firm and refuse for every other — see `independence.py`.
      engagementState: demo.letter.engagementState,
      clientConsentOnFile: demo.letter.clientConsentOnFile,
    },
    issuer: {
      ...EMPTY_ISSUER_FORM,
      attestedNoImpairingInterest: demo.issuer.attestedNoImpairingInterest,
      licensedForAttest: demo.issuer.licensedForAttest,
      peerReviewCurrent: demo.issuer.peerReviewCurrent,
      peerReviewExpiry: demo.issuer.peerReviewExpiry,
      isPlatformBackOffice: demo.issuer.isPlatformBackOffice,
      declaredInterests: [],
    },
  }
}

/**
 * Has anyone typed anything yet?
 *
 * The demo button is offered only while this is true, because loading it
 * overwrites all four forms at once and there is no undo. A CPA who has begun
 * a real letter must not be one mis-click from losing it — so the affordance
 * removes itself rather than asking "are you sure", which is the same guarantee
 * with one fewer thing to read.
 *
 * Deliberately coarse: any statement text, any assertion, any cited reference,
 * any named subject or addressee, any filled input, or any typed request line
 * counts as work. The checkbox defaults are not consulted — a tick the CPA
 * never touched is indistinguishable from one they did, and guessing wrong in
 * that direction hides the button rather than destroying anything.
 */
export function isPristine(
  classify: ClassifyFormState,
  letter: LetterFormState,
): boolean {
  if (classify.requestedItemsText.trim()) return false
  if (letter.subjectEntity.trim() || letter.addressee.trim()) return false
  if (letter.intendedUsersText.trim() || letter.purpose.trim()) return false
  if (Object.values(letter.inputs).some(v => v.trim())) return false
  return letter.statements.every(
    s => !s.assertion_id.trim()
      && !s.text.trim()
      && s.evidence.every(e => !e.reference.trim() && !e.location.trim()),
  )
}
